import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import {
  BackofficeMetricUnit,
  FccOperatorAvailabilityType,
} from '@backoffice-frontend/graphql'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useSound from 'use-sound'

type AlertSoundsContextValue = {
  alertSoundsEnabled: boolean
  toggleAlertSounds: () => void
  playAlertSound: () => void
}

export const AlertSoundsContext = createContext<AlertSoundsContextValue | null>(
  null,
)

const DEBOUNCE_MS = 1000

export const useAlertSoundsProvider = (): AlertSoundsContextValue => {
  const { availability } = useAdOperatorAvailability()
  const { trackMetric } = useMetricTracking()
  const isOnDuty = availability !== FccOperatorAvailabilityType.Offline
  const prevIsOnDutyRef = useRef(isOnDuty)
  const lastPlayTimeRef = useRef(0)
  const stateStartTimeRef = useRef(Date.now())

  const [alertSoundsEnabled, setAlertSoundsEnabled] = useState(isOnDuty)

  const [play] = useSound('/assets/sounds/prompt-notification.mp3', {
    volume: 1,
  })

  useEffect(() => {
    if (prevIsOnDutyRef.current !== isOnDuty) {
      const duration = Date.now() - stateStartTimeRef.current
      trackMetric({
        metricName: 'AlertSoundsStateDuration',
        value: duration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'state', value: alertSoundsEnabled ? 'enabled' : 'disabled' },
          {
            name: 'dutyStatus',
            value: prevIsOnDutyRef.current ? 'onDuty' : 'offDuty',
          },
        ],
      })
      stateStartTimeRef.current = Date.now()
      setAlertSoundsEnabled(isOnDuty)
      prevIsOnDutyRef.current = isOnDuty
    }
  }, [isOnDuty, alertSoundsEnabled, trackMetric])

  const toggleAlertSounds = useCallback(() => {
    setAlertSoundsEnabled(prev => {
      const duration = Date.now() - stateStartTimeRef.current
      trackMetric({
        metricName: 'AlertSoundsStateDuration',
        value: duration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'state', value: prev ? 'enabled' : 'disabled' },
          { name: 'dutyStatus', value: isOnDuty ? 'onDuty' : 'offDuty' },
        ],
      })
      stateStartTimeRef.current = Date.now()
      return !prev
    })
  }, [trackMetric, isOnDuty])

  const playAlertSound = useCallback(() => {
    const now = Date.now()
    if (alertSoundsEnabled && now - lastPlayTimeRef.current > DEBOUNCE_MS) {
      lastPlayTimeRef.current = now
      play()
    }
  }, [alertSoundsEnabled, play])

  return useMemo(
    () => ({
      alertSoundsEnabled,
      toggleAlertSounds,
      playAlertSound,
    }),
    [alertSoundsEnabled, toggleAlertSounds, playAlertSound],
  )
}

export const useAlertSounds = (): AlertSoundsContextValue => {
  const context = useContext(AlertSoundsContext)
  if (!context) {
    throw new Error('useAlertSounds must be used within AlertSoundsProvider')
  }
  return context
}
